import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/new-components/ui/card';
import { Button } from '../components/new-components/ui/button';
import { Input } from '../components/new-components/ui/input';
import { Label } from '../components/new-components/ui/label';
import { sandboxOnboardingApi, type SandboxAcceptInfo } from '../services/sandboxApi';

// ─────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────
interface FormState {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
}

// ─────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────
export default function SandboxInvestorAccept() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [acceptInfo, setAcceptInfo] = useState<SandboxAcceptInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [form, setForm] = useState<FormState>({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // ── Fetch invite info on mount ──
  useEffect(() => {
    if (!token) {
      setLoadError('No invitation token found. Please use the link from your email.');
      setIsLoading(false);
      return;
    }

    sandboxOnboardingApi
      .getAcceptInfo(token)
      .then((res) => {
        if (res.success && res.data) {
          if (!res.data.tokenValid) {
            setLoadError('This invitation link has expired or is invalid. Please contact the platform administrator.');
          } else {
            setAcceptInfo(res.data);
          }
        } else {
          setLoadError(res.message || res.error || 'Invitation link is invalid or has expired.');
        }
      })
      .catch(() => {
        setLoadError('Failed to load invitation. Please try again.');
      })
      .finally(() => setIsLoading(false));
  }, [token]);

  function handleChange(field: keyof FormState) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
      setFormError(null);
    };
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!acceptInfo) return;

    // Validate
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setFormError('First and last name are required.');
      return;
    }
    if (form.password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    if (form.password !== form.confirmPassword) {
      setFormError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const res = await sandboxOnboardingApi.acceptInvite({
        token,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        password: form.password,
      });

      if (res.success && res.data) {
        localStorage.setItem('authToken', res.data.token);
        setIsSuccess(true);
        // Brief delay so user sees the success state before redirect
        const slug = acceptInfo?.tenantId || res.data.user?.sandboxTenantId || 'portal';
        setTimeout(() => navigate(`/${slug}/portal`), 1500);
      } else {
        setFormError(res.message || res.error || 'Failed to accept invitation. Please try again.');
      }
    } catch {
      setFormError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // ── Loading state ──
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Error state ──
  if (loadError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Invitation Unavailable</h2>
            <p className="text-muted-foreground text-sm">{loadError}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!acceptInfo) return null;

  const accentColor = acceptInfo.primaryColor || '#1a1a2e';

  // ── Success state ──
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">Account created!</h2>
            <p className="text-muted-foreground text-sm">Redirecting you to your investor portal…</p>
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground mx-auto" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Main form ──
  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-4 py-10">
      {/* Branded header */}
      <div className="mb-6 text-center">
        {acceptInfo.logoUrl ? (
          <img
            src={acceptInfo.logoUrl}
            alt={acceptInfo.tenantName}
            className="h-12 object-contain mx-auto mb-3"
          />
        ) : (
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-white font-bold text-xl"
            style={{ backgroundColor: accentColor }}
          >
            {acceptInfo.tenantName.charAt(0).toUpperCase()}
          </div>
        )}
        <h1 className="text-2xl font-semibold text-foreground" style={{ color: accentColor }}>
          {acceptInfo.tenantName}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">You've been invited to the investor portal.</p>
      </div>

      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <div className="grid grid-cols-2 gap-3">
              {/* First Name */}
              <div className="space-y-1.5">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  placeholder="Jane"
                  value={form.firstName}
                  onChange={handleChange('firstName')}
                  autoComplete="given-name"
                  required
                />
              </div>
              {/* Last Name */}
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  placeholder="Doe"
                  value={form.lastName}
                  onChange={handleChange('lastName')}
                  autoComplete="family-name"
                  required
                />
              </div>
            </div>

            {/* Email — pre-filled, read-only */}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={acceptInfo.email}
                readOnly
                disabled
                className="bg-muted text-muted-foreground cursor-not-allowed"
              />
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Min. 8 characters"
                value={form.password}
                onChange={handleChange('password')}
                autoComplete="new-password"
                required
              />
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Re-enter password"
                value={form.confirmPassword}
                onChange={handleChange('confirmPassword')}
                autoComplete="new-password"
                required
              />
            </div>

            {/* Form error */}
            {formError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full"
              style={{ backgroundColor: accentColor, borderColor: accentColor }}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating account…
                </>
              ) : (
                'Create Account & Sign In'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
